import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { ComposedChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import { API, formatBg, formatDelta, DIRECTION_ARROWS, bgStatus, statusColor, statusLabel } from "../lib/glucose";
import { Waveform, LinkSimple } from "@phosphor-icons/react";

// Public read-only patient snapshot. NO auth. NO app shell.
export default function SharedPatient() {
  const { slug } = useParams();
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState(null);
  const [hours, setHours] = useState(24);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await fetch(`${API}/share/${slug}?hours=${hours}`);
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          if (!cancelled) setErr(d.detail || `HTTP ${r.status}`);
          return;
        }
        const data = await r.json();
        if (!cancelled) { setSnap(data); setErr(null); }
      } catch (e) {
        if (!cancelled) setErr("Network error");
      }
    }
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [slug, hours]);

  if (err) return <ErrorScreen msg={err} />;
  if (!snap) return <LoadingScreen />;

  const { patient, current, entries, stats, label, expires_at } = snap;
  const units = patient.units;
  const alertLow = patient.alert_low;
  const alertHigh = patient.alert_high;
  const targetLow = patient.target_low;
  const targetHigh = patient.target_high;

  const currentStatus = current ? bgStatus(current.sgv, alertLow, alertHigh) : "neutral";
  const currentColor = statusColor(currentStatus);
  const currentArrow = current?.direction ? DIRECTION_ARROWS[current.direction] : "";
  const minutesAgo = current ? Math.round((Date.now() - current.date) / 60000) : null;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="shared-root">
      <header className="border-b border-border/60 bg-background sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-foreground text-background grid place-items-center">
            <Waveform size={18} weight="duotone" />
          </div>
          <div className="font-display font-semibold text-lg tracking-tight">GlucoCloud</div>
          <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-border text-muted-foreground flex items-center gap-1">
            <LinkSimple size={11} /> Read-only
          </span>
          <div className="ml-auto flex items-center gap-2">
            {[3, 6, 12, 24, 48, 168].map((h) => (
              <button
                key={h}
                onClick={() => setHours(h)}
                className={`px-2 h-7 rounded-md text-xs ${hours === h ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`shared-range-${h}`}
              >
                {h < 24 ? `${h}h` : `${h / 24}d`}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8" data-testid="shared-main">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Shared with you</div>
          <h1 className="font-display text-4xl tracking-tight" data-testid="shared-patient-name">
            {label || patient.name}
          </h1>
          {expires_at && (
            <div className="text-xs text-muted-foreground mt-1">
              This link expires {new Date(expires_at).toLocaleDateString()}
            </div>
          )}
        </div>

        {/* Current reading */}
        <section className="grid md:grid-cols-3 gap-4">
          <div className="p-6 rounded-lg border border-border bg-card md:col-span-1" data-testid="shared-current-card">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Current</div>
            {current ? (
              <>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-display text-6xl tabular" style={{ color: currentColor }}>
                    {formatBg(current.sgv, units)}
                  </span>
                  <span className="text-2xl" style={{ color: currentColor }}>{currentArrow}</span>
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {units} · {current.delta != null ? formatDelta(current.delta, units) : "—"}
                </div>
                <div className="mt-4 flex items-center gap-2 text-xs">
                  <span
                    className="w-1.5 h-1.5 rounded-full pulse-dot"
                    style={{ background: currentColor }}
                  />
                  <span style={{ color: currentColor }}>{statusLabel(currentStatus)}</span>
                  <span className="text-muted-foreground">· {minutesAgo}m ago</span>
                </div>
              </>
            ) : (
              <div className="mt-6 text-sm text-muted-foreground">No recent readings.</div>
            )}
          </div>

          {stats && (
            <div className="p-6 rounded-lg border border-border bg-card md:col-span-2 grid grid-cols-3 gap-6" data-testid="shared-stats">
              <StatBox label="Time in range" value={`${stats.tir}%`} accent="sig-in-range" />
              <StatBox label="Average" value={formatBg(stats.avg, units)} sub={units} />
              <StatBox label="GMI" value={stats.gmi} sub="est. HbA1c proxy" />
            </div>
          )}
        </section>

        {/* Chart */}
        <section className="p-6 rounded-lg border border-border bg-card" data-testid="shared-chart-card">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-lg">Glucose · {hours < 24 ? `${hours}h` : `${hours / 24}d`}</div>
            <div className="text-xs text-muted-foreground">{entries.length} readings</div>
          </div>
          <div className="h-72">
            {entries.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">No readings in this range.</div>
            ) : (
              <ChartInner entries={entries} alertLow={alertLow} alertHigh={alertHigh} targetLow={targetLow} targetHigh={targetHigh} units={units} />
            )}
          </div>
        </section>

        <footer className="text-center text-xs text-muted-foreground pt-6 border-t border-border/60">
          Powered by <span className="font-medium text-foreground">GlucoCloud</span> · This is a read-only view. Values may be delayed.
        </footer>
      </main>
    </div>
  );
}

function StatBox({ label, value, sub, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`font-display text-3xl tabular mt-1 ${accent || ""}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function LoadingScreen() {
  return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground" data-testid="shared-loading">Loading…</div>;
}

function ErrorScreen({ msg }) {
  return (
    <div className="min-h-screen grid place-items-center px-6" data-testid="shared-error">
      <div className="max-w-md text-center">
        <div className="text-4xl mb-3">🔒</div>
        <div className="font-display text-2xl mb-2">Can&apos;t open this link</div>
        <div className="text-sm text-muted-foreground">{msg}</div>
      </div>
    </div>
  );
}

function ChartInner({ entries, alertLow, alertHigh, targetLow, targetHigh, units }) {
  const RED = "hsl(var(--sig-urgent))";
  const YELLOW = "hsl(var(--sig-high))";
  const GREEN = "hsl(var(--sig-in-range))";
  const MUTED = "hsl(var(--muted-foreground))";

  const data = useMemo(() => {
    const sorted = [...entries].sort((a, b) => a.date - b.date);
    return sorted.map((e) => ({ date: e.date, sgv: e.sgv }));
  }, [entries]);

  const yTick = (v) => (units === "mmol/L" ? (v * 0.0555).toFixed(1) : Math.round(v));

  return (
    <ResponsiveContainer>
      <ComposedChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="date"
          type="number"
          domain={["dataMin", "dataMax"]}
          tickFormatter={(t) => {
            const d = new Date(t);
            return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
          }}
          stroke={MUTED}
          fontSize={11}
        />
        <YAxis domain={[40, 400]} tickFormatter={yTick} stroke={MUTED} fontSize={11} width={40} />
        <Tooltip
          contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: "12px" }}
          labelFormatter={(t) => new Date(t).toLocaleString()}
          formatter={(v) => [`${formatBg(v, units)} ${units}`, "glucose"]}
        />
        <ReferenceLine y={alertLow} stroke={RED} strokeDasharray="3 3" opacity={0.7} />
        <ReferenceLine y={targetLow} stroke={MUTED} strokeDasharray="2 4" opacity={0.5} />
        <ReferenceLine y={targetHigh} stroke={MUTED} strokeDasharray="2 4" opacity={0.5} />
        <ReferenceLine y={alertHigh} stroke={YELLOW} strokeDasharray="3 3" opacity={0.7} />
        <Line type="monotone" dataKey="sgv" stroke={GREEN} strokeWidth={2} dot={false} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
